/**
 * DeletePlayerDialog Container Component
 *
 * Manages close-guard logic (prevents dismissal while deleting) and
 * derives the optional report-count warning body line. The actual delete
 * mutation is owned by the parent (PlayerPageHeaderInnerContainer).
 *
 * Renders the shared ConfirmDialog molecule (SE-5524); the
 * former bespoke AlertDialog View is standardized onto the molecule. The
 * legacy `delete-player-cancel|confirm` testIDs are produced by the
 * molecule's `${testID}-cancel|confirm` derivation.
 * @module features/player-detail/components/DeletePlayerDialog
 */

import { AlertTriangle } from "lucide-react-native";
import { useCallback } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Props for DeletePlayerDialogContainer */
export interface DeletePlayerDialogContainerProps {
  /** Whether the dialog is visible */
  readonly isOpen: boolean;
  /** Whether deletion is in progress */
  readonly isDeleting: boolean;
  /** Display name of the player to delete */
  readonly playerName: string;
  /** Number of scout reports attached to the player */
  readonly scoutReportsCount: number;
  /** Callback invoked when the dialog is dismissed */
  readonly onClose: () => void;
  /** Async callback invoked when user confirms deletion */
  readonly onConfirmDelete: () => Promise<void>;
}

/**
 * Container component for the delete player confirmation dialog
 * @param root0 - Component props
 * @param root0.isOpen - Whether the dialog is visible
 * @param root0.isDeleting - Whether deletion is in progress
 * @param root0.playerName - Display name of the player to delete
 * @param root0.scoutReportsCount - Number of scout reports attached
 * @param root0.onClose - Callback invoked when the dialog is dismissed
 * @param root0.onConfirmDelete - Async callback invoked when user confirms deletion
 */
const DeletePlayerDialogContainer = ({
  isOpen,
  isDeleting,
  playerName,
  scoutReportsCount,
  onClose,
  onConfirmDelete,
}: DeletePlayerDialogContainerProps) => {
  const handleClose = useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await onConfirmDelete();
    } catch (_error) {
      // Error handling is managed by parent component
      console.warn("Delete player failed:", _error);
    }
  }, [onConfirmDelete]);

  const body = [
    `Are you sure you want to delete “${playerName}”?`,
    "This action cannot be undone.",
    ...(scoutReportsCount > 0
      ? [
          `This player has ${scoutReportsCount} scout report${
            scoutReportsCount !== 1 ? "s" : ""
          } attached. Deleting will remove access to these reports.`,
        ]
      : []),
  ];

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirmDelete}
      title="Delete Player"
      body={body}
      icon={AlertTriangle}
      tone="danger"
      confirmLabel={isDeleting ? "Deleting..." : "Delete Player"}
      isConfirming={isDeleting}
      testID="delete-player"
    />
  );
};

DeletePlayerDialogContainer.displayName = "DeletePlayerDialogContainer";

export default DeletePlayerDialogContainer;
